import { Form, FormErrorTypes, type ITextareaProps } from 'dash-ui';
import { PropertyValidation } from 'dash-common';
import { useTranslation } from 'react-i18next';
import type { ChangeEvent, FC } from 'react';

import { type IComponentProps } from 'containers/properties/AdvancedPropertyDetails/AdvancedPropertyDetails.interfaces';
import { FormFields } from 'containers/properties/AdvancedPropertyDetails/AdvancedPropertyDetails.constants';
import { StyledBox } from 'containers/properties/AdvancedPropertyDetails/AdvancedPropertyDetails.styles';

const DescriptionFormElements: FC<IComponentProps & ITextareaProps> = ({
  formErrors,
  formHandlers,
  formState,
  ...textareaProps
}) => {
  const { t } = useTranslation('forms');
  const validate = (
    field: FormFields,
    value: string,
    { max }: { max: number }
  ) => {
    if (value.trim().length > max) {
      formHandlers[field].setError({
        type: FormErrorTypes.Invalid,
        message: t('messages.maxCharacterLimit', { number: max }),
      });
      return false;
    }
    formHandlers[field].setError(null);

    return true;
  };

  const handleTextAreas =
    (field: FormFields, { max }: { max: number }) =>
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = e.target;
      formHandlers[field].setState(value);
      validate(field, value, { max });
    };

  return (
    <StyledBox w="100%" mt={6} pb={6} px={6}>
      <Form.Group>
        <Form.Label>{t('labels.overview')}</Form.Label>
        <Form.Textarea
          minHeight={160}
          onChange={handleTextAreas(FormFields.Overview, {
            max: PropertyValidation.DefaultMaxDescriptionsLength,
          })}
          value={formState?.overview || ''}
          placeholder={
            t('placeholders.overviewPlaceholder', {
              number: PropertyValidation.DefaultMaxDescriptionsLength,
            }) || ''
          }
          $errorType={formErrors[FormFields.Overview]?.type}
          {...textareaProps}
        />
        <Form.Feedback $errorType={formErrors[FormFields.Overview]?.type}>
          {formErrors[FormFields.Overview]?.message}
        </Form.Feedback>
      </Form.Group>
      <Form.Group mt={4}>
        <Form.Label>{t('labels.projectDetails')}</Form.Label>
        <Form.Textarea
          minHeight={144}
          onChange={handleTextAreas(FormFields.Project_Details, {
            max: PropertyValidation.DefaultMaxDescriptionsLength,
          })}
          value={formState?.projectDetails || ''}
          placeholder={
            t('placeholders.projectDetailsPlaceholder', {
              number: PropertyValidation.DefaultMaxDescriptionsLength,
            }) || ''
          }
          $errorType={formErrors[FormFields.Project_Details]?.type}
          disabled
          {...textareaProps}
        />
        <Form.Feedback
          $errorType={formErrors[FormFields.Project_Details]?.type}
        >
          {formErrors[FormFields.Project_Details]?.message}
        </Form.Feedback>
      </Form.Group>
    </StyledBox>
  );
};

export default DescriptionFormElements;
